import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronRight, ChevronLeft } from 'lucide-react';
import { MONTHS_AF, DAYS_SHORT, toJalali, toGregorian, daysInMonth, todayJalali } from '../../utils/dateUtils';

export default function AfghanDatePicker({ label, value, onChange, error, placeholder = 'انتخاب تاریخ', disabled = false }) {
  const jToday = todayJalali();
  const getParsed = () => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return toJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
  };
  const parsed = getParsed();
  const [open, setOpen] = useState(false);
  const [viewY, setViewY] = useState(() => parsed?.[0] || jToday[0]);
const [viewM, setViewM] = useState(() => parsed?.[1] || jToday[1]);
  const ref = useRef();

  useEffect(() => {
    const p = getParsed();
    if (p) { setViewY(p[0]); setViewM(p[1]); }
  }, [value]);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const prevMonth = () => { if (viewM === 1) { setViewY(y => y - 1); setViewM(12); } else setViewM(m => m - 1); };
  const nextMonth = () => { if (viewM === 12) { setViewY(y => y + 1); setViewM(1); } else setViewM(m => m + 1); };

  const selectDay = (d) => {
    if (!d) return;
    const [gy, gm, gd] = toGregorian(viewY, viewM, d);
    onChange(`${gy}-${String(gm).padStart(2,'0')}-${String(gd).padStart(2,'0')}`);
    setOpen(false);
  };

  const selectToday = () => {
    const d = new Date();
    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const [jy, jm] = toJalali(d.getFullYear(), d.getMonth()+1, d.getDate());
    setViewY(jy); setViewM(jm);
    onChange(iso); setOpen(false);
  };

  const totalDays = daysInMonth(viewY, viewM);
  const [gY, gM, gD] = toGregorian(viewY, viewM, 1);
  const startOffset = (new Date(gY, gM - 1, gD).getDay() + 1) % 7;
  const cells = Array(startOffset).fill(null).concat(Array.from({ length: totalDays }, (_, i) => i + 1));
  const years = Array.from({ length: 20 }, (_, i) => jToday[0] - 10 + i);
  const displayValue = parsed ? `${parsed[0]}/${String(parsed[1]).padStart(2,'0')}/${String(parsed[2]).padStart(2,'0')} — ${MONTHS_AF[parsed[1]-1]}` : '';

  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>}
      <div onClick={() => !disabled && setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm border rounded-lg bg-white select-none transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${error ? 'border-red-400' : open ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-blue-400'}`}>
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={`flex-1 ${displayValue ? 'text-gray-900' : 'text-gray-400'}`}>{displayValue || placeholder}</span>
        {displayValue && !disabled && (
          <button type="button" onClick={e => { e.stopPropagation(); onChange(''); }} className="text-gray-300 hover:text-gray-500 text-xs px-1">✕</button>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {open && (
        <div className="absolute top-full mt-1 right-0 z-[200] bg-white border border-gray-200 rounded-xl shadow-2xl w-72">
          <div className="bg-blue-600 px-3 py-2 flex items-center justify-between rounded-t-xl">
            <button type="button" onClick={nextMonth} className="text-white/80 hover:text-white p-1"><ChevronRight className="w-4 h-4" /></button>
            <div className="flex items-center gap-1">
              <select value={viewM} onChange={e => setViewM(+e.target.value)} className="text-white bg-blue-700 text-xs font-bold border-none outline-none rounded px-2 py-1 cursor-pointer">
                {MONTHS_AF.map((m, i) => <option key={i} value={i+1} className="text-gray-900 bg-white">{m}</option>)}
              </select>
              <select value={viewY} onChange={e => setViewY(+e.target.value)} className="text-white bg-blue-700 text-xs font-bold border-none outline-none rounded px-2 py-1 cursor-pointer">
                {years.map(y => <option key={y} value={y} className="text-gray-900 bg-white">{y}</option>)}
              </select>
            </div>
            <button type="button" onClick={prevMonth} className="text-white/80 hover:text-white p-1"><ChevronLeft className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 px-2 pt-2">
            {DAYS_SHORT.map(d => <div key={d} className="text-center text-[10px] text-gray-400 font-bold py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5 px-2 pb-2">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const isToday = viewY===jToday[0] && viewM===jToday[1] && d===jToday[2];
              const isSel = parsed && viewY===parsed[0] && viewM===parsed[1] && d===parsed[2];
              return (
                <button key={i} type="button" onClick={() => selectDay(d)}
                  className={`w-full aspect-square text-xs rounded-lg font-medium transition-all ${isSel ? 'bg-blue-600 text-white' : isToday ? 'bg-blue-100 text-blue-700 font-bold' : 'hover:bg-gray-100 text-gray-700'}`}>
                  {d}
                </button>
              );
            })}
          </div>
          <div className="border-t px-3 py-2">
            <button type="button" onClick={selectToday} className="w-full py-1.5 text-xs text-blue-600 font-semibold hover:bg-blue-50 rounded-lg">
              امروز — {jToday[0]}/{String(jToday[1]).padStart(2,'0')}/{String(jToday[2]).padStart(2,'0')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
